<script lang="ts">
    import {createEventDispatcher} from 'svelte';
    import {isEqual, omitBy} from 'lodash';
    import {isNullOrUndefined, uuid as generateUuid} from '../../../shared/utils/custom-utils';
    import {pipe} from '../../../svelte/utils';
    import {loc} from '../../../svelte/pipes';
    import type {Form} from '../../../typings/portaro.be.types';
    import type {SearchFiltersEventsMap} from './events';
    import {getLocalization, getLogger} from '../../../svelte/context';
    import KpSearchAdditionalFilterField from './KpSearchAdditionalFilterField.svelte';

    export let filters: Form[];
    export let params: Record<string, any>;

    const logger = getLogger();
    const dispatch = createEventDispatcher<SearchFiltersEventsMap>();
    const localize = getLocalization();

    initializeEmptyParamProperties();

    let lastSubmittedParams;

    let isComponentVisible: boolean
    $: isComponentVisible = !isNullOrUndefined(filters) && Array.isArray(filters) && filters.length > 0;

    $: if (!haveUniqueFormIds(filters)) {
        filters = filters.map((filter) => ({...filter, id: generateUuid()}));
        logger.warn('kp-search-additional-filters: forms do not have unique ids - generating random ids as fallback')
    }

    function submitForm() {
        const trimmedParams = omitBy(params, (value) => value === null);
        if (!isEqual(trimmedParams, lastSubmittedParams)) {
            dispatch('submit-search');
            lastSubmittedParams = trimmedParams;
        }
    }

    function updateParams() {
        dispatch('params-change', params);
    }

    function haveUniqueFormIds(forms: Form[]) {
        forms = isNullOrUndefined(forms) ? [] : forms;
        const allIds = forms.map((form) => form.id);
        const uniqueIds = [...new Set(allIds)];

        return allIds.length === uniqueIds.length;
    }

    function initializeEmptyParamProperties() {
        // add properties missing in params, otherwise svelte will warn about KpValueEditor created with missing model prop
        filters.flatMap((filter) => filter.fields).forEach((field) => {
            if (!Object.hasOwn(params, field.fieldName)) {
                params[field.fieldName] = null;
            }
        })
    }
</script>

<template>
    {#if isComponentVisible}
        <form class="search-additional-filter-container"
              name="search-filters"
              aria-label={localize(/* @kp-localization search.accessibility.searchResultFilters */ 'search.accessibility.searchResultFilters')}
              on:submit|preventDefault={submitForm}>

            <div class="panel panel-default">
                <div class="facets-panel-body">

                    {#each filters as filter (filter.id)}
                        <div class="facet" role="group" aria-labelledby="{filter.id}-label">
                            <div id="{filter.id}-label" class="facet-name">
                                {pipe(filter, loc())}
                            </div>

                            {#each filter.fields as field (field.editor.editorId ?? field.fieldName)}
                                <KpSearchAdditionalFilterField {field}
                                                               bind:model={params[field.fieldName]}
                                                               isSingleField={filter.fields.length <= 1}
                                                               on:model-change={updateParams}/>
                            {/each}
                        </div>
                    {/each}
                </div>
            </div>

            <div class="search-button facets-panel-body">
                <div class="facet">
                    <div class="facet-name">
                        <button class="btn btn-primary" type="submit">
                            {localize(/* @kp-localization commons.hledat */ 'commons.hledat')}
                        </button>
                    </div>
                </div>
            </div>
        </form>
    {/if}
</template>

<style lang="less">
    @import "bootstrap-less/bootstrap/variables";

    .panel-default {
        margin-bottom: 0;
        border-bottom: 0;
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
        box-shadow: none;
    }

    .facets-panel-body {

        .facet-name {
            border-top: 0;
        }

        &.search-button {
            position: sticky;
            bottom: 0;
            z-index: 2; // to ensure it will be at least at the same level as inputs and form controls
            background: linear-gradient(to bottom, transparent 0%, white 11px, white 100%);
            padding-top: 10px;
            border-width: 0 1px 1px;
            border-style: solid;
            border-color: @panel-default-border;
            border-bottom-left-radius: @panel-border-radius;
            border-bottom-right-radius: @panel-border-radius;
            margin-bottom: 20px;

            .facet-name {
                padding: 10px 10px 0 10px;

                .btn {
                    width: 100%;
                }
            }
        }
    }
</style>